import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Modal, Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import MediaUpload from '../../Business/MediaUpload'
import useApi from '../../../hooks/useApi'
import { businessMediaUrl } from '../../../pages/business/settings/businessMedia'

const COVER_PHOTO_DIMENSIONS = '1920x600'

export default function BusinessCoverPhotosModal({
  show,
  onHide,
  business,
  onSaved,
}) {
  const { t } = useTranslation()
  const { updateBusiness } = useApi()

  const [pendingCover, setPendingCover] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!show) {
      setPendingCover(null)
      setError(null)
      setIsSaving(false)
    }
  }, [show])

  const previewUrl = pendingCover?.id
    ? pendingCover.thumbnail_url ?? pendingCover.direct_upload_url
    : businessMediaUrl(business, 'cover_photo', 'cover_photo_id')

  const hasChanged =
    pendingCover?.id != null &&
    pendingCover.id !== business?.cover_photo?.id &&
    pendingCover.id !== business?.cover_photo_id

  const handleChange = useCallback(mediaAttachment => {
    // MediaUpload remove calls onChange(null). Treat that as canceling a
    // pending upload (API cannot clear cover_photo_id), not as a saveable change.
    setPendingCover(mediaAttachment?.id ? mediaAttachment : null)
    setError(null)
  }, [])

  const handleSave = useCallback(async () => {
    if (!business?.id || !pendingCover?.id || isSaving) return
    setIsSaving(true)
    setError(null)
    try {
      const response = await updateBusiness(business.id, {
        cover_photo_id: pendingCover.id,
      })
      if (response?.errors || response?.error) {
        setError(
          response.errors?.full_messages?.[0] ||
            t('business.edit.save_failed', 'Failed to save. Please try again.'),
        )
        return
      }
      const nextCover =
        response?.data?.cover_photo !== undefined
          ? response.data.cover_photo
          : pendingCover
      const nextCoverId =
        response?.data?.cover_photo_id !== undefined
          ? response.data.cover_photo_id
          : pendingCover.id
      if (onSaved) {
        onSaved({
          cover_photo: nextCover,
          cover_photo_id: nextCoverId,
        })
      }
      onHide()
    } catch {
      setError(
        t(
          'business.edit.save_failed_retry',
          'Failed to save. Please try again.',
        ),
      )
    } finally {
      setIsSaving(false)
    }
  }, [business?.id, pendingCover, isSaving, updateBusiness, onSaved, onHide, t])

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('business.edit.cover_photo_title', 'Business Page Cover Photo')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <MediaUpload
          imageUrl={previewUrl}
          onChange={handleChange}
          label={t(
            'business.edit.cover_photo_label',
            'Business Page Cover Photo',
          )}
          dimensions={COVER_PHOTO_DIMENSIONS}
        />
        {error ? <p className="text-danger mt-2 mb-0">{error}</p> : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" type="button" onClick={onHide}>
          {t('buttons.cancel', 'Cancel')}
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={handleSave}
          disabled={!hasChanged || isSaving}
        >
          {isSaving
            ? t('users.edit.saving', 'Saving...')
            : t('business.edit.save', 'Save')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

BusinessCoverPhotosModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  business: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    cover_photo: PropTypes.object,
    cover_photo_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onSaved: PropTypes.func,
}
